import { useState, useEffect } from 'react';
import { 
    Clock, 
    AlertTriangle, 
    CheckCircle, 
    Gauge,
    RefreshCw,
    Truck,
    Timer,
    Play,
    Square
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getProgressColor = (remaining, total) => {
    const percent = (remaining / total) * 100;
    if (percent <= 20) return 'bg-red-500';
    if (percent <= 50) return 'bg-yellow-500';
    return 'bg-green-500';
};

export default function HOSStatusCard({ truckId, onRefresh, refreshTrigger, autoRefresh = false }) {
    const [hosStatus, setHosStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    
    // Reset timer state
    const [resetTimer, setResetTimer] = useState('00:00:00');
    const [resetElapsedHours, setResetElapsedHours] = useState(0);

    // Initial fetch and refresh trigger
    useEffect(() => {
        if (truckId) {
            fetchHOSStatus();
        }
    }, [truckId, refreshTrigger]);

    // Auto-refresh polling (every 10 seconds when enabled)
    useEffect(() => {
        if (!truckId || !autoRefresh) return;
        
        const interval = setInterval(() => {
            fetchHOSStatus();
        }, 10000); // Refresh every 10 seconds
        
        return () => clearInterval(interval);
    }, [truckId, autoRefresh]);
    
    // Update reset timer every second when reset is in progress
    useEffect(() => {
        if (!hosStatus?.reset_in_progress || !hosStatus?.reset_start_time) {
            setResetTimer('00:00:00');
            setResetElapsedHours(0);
            return;
        }
        
        const updateTimer = () => {
            try {
                const start = new Date(hosStatus.reset_start_time);
                const now = new Date();
                const elapsed = Math.floor((now - start) / 1000);
                const hours = Math.floor(elapsed / 3600);
                const minutes = Math.floor((elapsed % 3600) / 60);
                const seconds = elapsed % 60;
                setResetTimer(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
                setResetElapsedHours(elapsed / 3600);
            } catch (e) {
                setResetTimer('00:00:00');
                setResetElapsedHours(0);
            }
        };
        
        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [hosStatus?.reset_in_progress, hosStatus?.reset_start_time]);

    const fetchHOSStatus = async () => {
        if (!truckId) return;
        
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/hos-simulation/duty-state/${truckId}`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setHosStatus(data);
            }
        } catch (error) {
            console.error('[HOS] Error fetching HOS status:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStartReset = async () => {
        setActionLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/hos-simulation/duty-state/${truckId}/start-reset`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                const result = await response.json();
                toast.success('Reset started - driver is now OFF DUTY', {
                    description: `Timer started at ${new Date(result.reset_start_time).toLocaleTimeString()}`
                });
                await fetchHOSStatus();
                if (onRefresh) onRefresh();
            } else {
                const error = await response.json();
                toast.error('Failed to start reset', { description: error.detail || 'Unknown error' });
            }
        } catch (error) {
            toast.error('Error starting reset', { description: error.message });
        } finally {
            setActionLoading(false);
        }
    };

    const handleEndReset = async () => {
        setActionLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/hos-simulation/duty-state/${truckId}/end-reset`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                const result = await response.json();
                const resetType = result.reset_type;
                let description = '';
                if (resetType === '34hr') {
                    description = 'All clocks reset (drive, duty, and cycle)';
                } else if (resetType === '10hr') {
                    description = 'Daily clocks reset (drive and on-duty)';
                } else {
                    description = 'Reset too short - no clock changes applied';
                }
                toast.success(`Reset ended - ${result.reset_duration_hours?.toFixed(1) || '0'}h recorded`, {
                    description
                });
                await fetchHOSStatus();
                if (onRefresh) onRefresh();
            } else {
                const error = await response.json();
                toast.error('Failed to end reset', { description: error.detail || 'Unknown error' });
            }
        } catch (error) {
            toast.error('Error ending reset', { description: error.message });
        } finally {
            setActionLoading(false);
        }
    };

    if (!truckId) {
        return (
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 text-center text-zinc-500">
                    Select a truck to view HOS status
                </CardContent>
            </Card>
        );
    }

    if (loading) {
        return (
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                </CardContent>
            </Card>
        );
    }

    if (!hosStatus) {
        return (
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 text-center text-zinc-500">
                    No HOS data available
                </CardContent>
            </Card>
        );
    }

    const resetInProgress = hosStatus.reset_in_progress;

    return (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="hos-status-card">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-zinc-300 uppercase tracking-wider flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Gauge className="w-4 h-4 text-orange-500" />
                        HOS Status
                    </div>
                    <div className="flex items-center gap-2">
                        {hosStatus.needs_34hr_reset && !resetInProgress && (
                            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                                RESET REQUIRED
                            </Badge>
                        )}
                        <Badge className={`text-xs ${
                            hosStatus.duty_status === 'driving' ? 'bg-green-500/20 text-green-400' :
                            hosStatus.duty_status === 'on_duty' ? 'bg-blue-500/20 text-blue-400' :
                            hosStatus.duty_status === 'sleeper' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-zinc-500/20 text-zinc-400'
                        }`}>
                            {hosStatus.duty_status?.replace('_', ' ').toUpperCase()}
                        </Badge>
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Reset Timer - Visible when driver is in reset */}
                {resetInProgress && (
                    <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-3 mb-2">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-blue-400 text-sm font-medium flex items-center gap-1">
                                <Timer className="w-4 h-4" />
                                Reset In Progress
                            </span>
                            <span className="text-blue-300 font-mono text-xl" data-testid={`hos-reset-timer-${truckId}`}>{resetTimer}</span>
                        </div>
                        <div className="flex gap-2">
                            {resetElapsedHours >= 10 ? (
                                <Badge className="bg-green-600/50 text-green-300 text-xs">10hr ✓</Badge>
                            ) : (
                                <Badge className="bg-zinc-600/50 text-zinc-400 text-xs">10hr: {(10 - resetElapsedHours).toFixed(1)}h left</Badge>
                            )}
                            {resetElapsedHours >= 34 ? (
                                <Badge className="bg-purple-600/50 text-purple-300 text-xs">34hr ✓</Badge>
                            ) : (
                                <Badge className="bg-zinc-600/50 text-zinc-400 text-xs">34hr: {(34 - resetElapsedHours).toFixed(1)}h left</Badge>
                            )}
                        </div>
                    </div>
                )}
                
                {/* Drive Hours Remaining */}
                <div>
                    <div className="flex justify-between text-xs text-zinc-400 mb-1">
                        <span>Drive Hours Remaining</span>
                        <span className={hosStatus.drive_hours_remaining <= 2 ? 'text-red-400 font-semibold' : ''}>
                            {hosStatus.drive_hours_remaining.toFixed(1)}h / 11h
                        </span>
                    </div>
                    <Progress 
                        value={(hosStatus.drive_hours_remaining / 11) * 100} 
                        className={`h-2 ${getProgressColor(hosStatus.drive_hours_remaining, 11)}`}
                    />
                </div>

                {/* On-Duty Window */}
                <div>
                    <div className="flex justify-between text-xs text-zinc-400 mb-1">
                        <span>On-Duty Window Remaining</span>
                        <span className={hosStatus.on_duty_window_remaining <= 2 ? 'text-red-400 font-semibold' : ''}>
                            {hosStatus.on_duty_window_remaining.toFixed(1)}h / 14h
                        </span>
                    </div>
                    <Progress 
                        value={(hosStatus.on_duty_window_remaining / 14) * 100} 
                        className={`h-2 ${getProgressColor(hosStatus.on_duty_window_remaining, 14)}`}
                    />
                </div>

                {/* Cycle Hours */}
                <div>
                    <div className="flex justify-between text-xs text-zinc-400 mb-1">
                        <span>Cycle Hours Remaining ({hosStatus.cycle_type})</span>
                        <span className={hosStatus.cycle_hours_remaining <= 10 ? 'text-red-400 font-semibold' : ''}>
                            {hosStatus.cycle_hours_remaining.toFixed(1)}h / {hosStatus.cycle_limit}h
                        </span>
                    </div>
                    <Progress 
                        value={(hosStatus.cycle_hours_remaining / hosStatus.cycle_limit) * 100} 
                        className={`h-2 ${getProgressColor(hosStatus.cycle_hours_remaining, hosStatus.cycle_limit)}`}
                    />
                </div>

                {/* Hours Until Break */}
                <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-400">Hours Until 30-Min Break Required</span>
                    <span className={`font-mono ${hosStatus.hours_until_break <= 1 ? 'text-yellow-400' : 'text-zinc-300'}`}>
                        {hosStatus.hours_until_break.toFixed(1)}h
                    </span>
                </div>

                {/* Start/End Reset Buttons */}
                <div className="flex gap-2 pt-2 border-t border-zinc-800">
                    {!resetInProgress ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleStartReset();
                            }}
                            disabled={actionLoading}
                            className="flex-1 border-blue-700 text-blue-400 hover:bg-blue-900/30 text-xs"
                            data-testid="hos-start-reset-btn"
                        >
                            <Play className="w-3 h-3 mr-1" />
                            Start Reset
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleEndReset();
                            }}
                            disabled={actionLoading}
                            className="flex-1 border-green-700 text-green-400 hover:bg-green-900/30 text-xs"
                            data-testid="hos-end-reset-btn"
                        >
                            <Square className="w-3 h-3 mr-1" />
                            End Reset
                        </Button>
                    )}
                </div>

                {/* Reset Duration Info */}
                <div className="text-xs text-zinc-500 text-center">
                    {resetInProgress ? (
                        <span>≥10h: resets daily clocks • ≥34h: resets all clocks</span>
                    ) : hosStatus.last_reset_time ? (
                        <span>Last reset: {new Date(hosStatus.last_reset_time).toLocaleDateString()}</span>
                    ) : null}
                </div>
            </CardContent>
        </Card>
    );
}
